import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { AuthResponse, ApiResponse } from '../types';

// Detectar URL da API (runtime no Easypanel ou build-time)
const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const runtime = (window as any).__VETTORPRINT_API_URL__;
    if (runtime && runtime !== '__API_URL_PLACEHOLDER__') return runtime;
  }
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined' && !window.location.hostname.includes('localhost')) return '/api';
  return 'http://localhost:5000/api';
};

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: getApiBaseUrl(),
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor: detect wrong proxy (API request returning HTML)
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        const ct = (response.headers?.['content-type'] ?? response.headers?.['Content-Type'] ?? '') as string;
        const isHtml = String(ct).toLowerCase().includes('text/html');
        const bodyLooksLikeHtml = typeof response.data === 'string' && response.data.trim().startsWith('<');
        if (isHtml || bodyLooksLikeHtml) {
          const err = new Error(
            'PROXY_ERR: O login está indo para o frontend em vez do backend. Use o subdomínio api.vettorchat.com.br para a API (veja EASYPANEL_PROXY_API.md).'
          ) as any;
          err.response = response;
          return Promise.reject(err);
        }
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints (aceita tanto { data: { user, token } } quanto { user, token })
  async login(email: string, password: string): Promise<AuthResponse> {
    const response = await this.api.post<ApiResponse<AuthResponse> | AuthResponse>('/auth/login', {
      email,
      password,
    });
    const data = response.data as ApiResponse<AuthResponse> & AuthResponse & string;
    // Resposta em HTML = proxy enviou requisição para o frontend
    if (typeof data === 'string' && data.trim().startsWith('<')) {
      throw new Error(
        'PROXY_ERR: O login está indo para o frontend em vez do backend. Use o subdomínio api.vettorchat.com.br para a API (veja EASYPANEL_PROXY_API.md).'
      );
    }
    const result = data?.data ?? data;
    if (!result?.user || !result?.token) {
      throw new Error('Resposta inválida do servidor de login. Confira se a API está em api.vettorchat.com.br e se o proxy está correto.');
    }
    return result;
  }

  async register(userData: any): Promise<AuthResponse> {
    const response = await this.api.post<ApiResponse<AuthResponse> | AuthResponse>('/auth/register', userData);
    const data = response.data as ApiResponse<AuthResponse> & AuthResponse;
    const result = data.data ?? data;
    if (!result?.user || !result?.token) {
      throw new Error('Resposta inválida do servidor ao registrar');
    }
    return result;
  }

  async getProfile(): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>('/auth/profile');
    return response.data.data;
  }

  async updateProfile(data: any): Promise<any> {
    const response = await this.api.put<ApiResponse<any>>('/auth/profile', data);
    return response.data.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.api.put('/auth/change-password', {
      currentPassword,
      newPassword,
    });
  }

  // Dashboard endpoints
  async getDashboardStats(): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>('/dashboard/stats');
    return response.data.data;
  }

  async getClientDistribution(month?: number, year?: number): Promise<any> {
    const params = new URLSearchParams();
    if (month) params.append('month', month.toString());
    if (year) params.append('year', year.toString());
    
    const response = await this.api.get<ApiResponse<any>>(`/dashboard/client-distribution?${params}`);
    return response.data.data;
  }

  async getDesignerFlowStatus(): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>('/dashboard/designer-flow');
    return response.data.data;
  }

  async getDesignerServiceInfo(): Promise<any[]> {
    const response = await this.api.get<ApiResponse<any[]>>('/dashboard/designer-service');
    return response.data.data;
  }

  async updateDesignerFlowStatus(designerId: string, status: 'GOOD_FLOW' | 'NORMAL' | 'BUSY' | 'SLOW'): Promise<any> {
    const response = await this.api.put<ApiResponse<any>>(`/dashboard/designer-flow/${designerId}`, { status });
    return response.data.data;
  }

  async getDesignerProductivity(period?: string, month?: number, year?: number): Promise<any> {
    const params = new URLSearchParams();
    if (period) params.append('period', period);
    if (month) params.append('month', month.toString());
    if (year) params.append('year', year.toString());

    const response = await this.api.get<ApiResponse<any>>(`/dashboard/designer-productivity?${params}`);
    return response.data.data;
  }

  async getMonthlyArtsHistory(months?: number): Promise<any> {
    const params = new URLSearchParams();
    if (months) params.append('months', months.toString());

    const response = await this.api.get<ApiResponse<any>>(`/dashboard/monthly-arts-history?${params}`);
    return response.data.data;
  }

  // User management endpoints
  async getUsers(): Promise<any[]> {
    const response = await this.api.get<ApiResponse<any[]>>('/users');
    return response.data.data;
  }

  /**
   * Lista enxuta de usuários ativos (id, name, role) para uso em selects.
   * Pode ser chamada por qualquer usuário autenticado.
   */
  async getSelectableUsers(): Promise<any[]> {
    const response = await this.api.get<ApiResponse<any[]>>('/users/selectable');
    return response.data.data;
  }

  async createUser(userData: {
    name: string;
    email: string;
    password: string;
    role: 'ADMIN' | 'SELLER' | 'DESIGNER' | 'SUPPORT' | 'MANAGER' | 'PRODUCAO';
  }): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>('/users', userData);
    return response.data.data;
  }

  async updateUser(id: string, userData: {
    name?: string;
    email?: string;
    role?: 'ADMIN' | 'SELLER' | 'DESIGNER' | 'SUPPORT' | 'MANAGER' | 'PRODUCAO';
    isActive?: boolean;
  }): Promise<any> {
    const response = await this.api.put<ApiResponse<any>>(`/users/${id}`, userData);
    return response.data.data;
  }

  async deleteUser(id: string): Promise<void> {
    await this.api.delete(`/users/${id}`);
  }

  async toggleUserStatus(id: string): Promise<any> {
    const response = await this.api.patch<ApiResponse<any>>(`/users/${id}/toggle-status`);
    return response.data.data;
  }

  async changeUserPassword(id: string, newPassword: string): Promise<any> {
    const response = await this.api.patch<ApiResponse<any>>(`/users/${id}/change-password`, {
      newPassword
    });
    return response.data.data;
  }

  // Order/Priority management endpoints
  async getAllOrders(): Promise<any[]> {
    const response = await this.api.get<ApiResponse<any[]>>('/orders');
    return response.data.data;
  }

  async getOrderById(id: string): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(`/orders/${id}`);
    return response.data.data;
  }

  async createOrder(orderData: {
    orderNumber: string;
    clientId: string;
    sellerId: string;
    deliveryDate: string;
    shippingMethod: string;
    contato?: string;
    freight?: string;
    categoria?: string;
    status?: string;
    priority?: string;
    observations?: string;
    reason?: string;
    artDescription?: string;
    value?: number;
    shippingValue?: number;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>('/orders', orderData);
    return response.data.data;
  }

  async updateOrder(id: string, orderData: {
    orderNumber?: string;
    clientPhone?: string;
    status?: string;
    priority?: string;
    deliveryDate?: string;
    shippingMethod?: string;
    freight?: string;
    categoria?: string;
    observations?: string;
    reason?: string;
    designerId?: string;
    supportId?: string;
  }): Promise<any> {
    const response = await this.api.put<ApiResponse<any>>(`/orders/${id}`, orderData);
    return response.data.data;
  }

  async deleteOrder(id: string): Promise<void> {
    await this.api.delete(`/orders/${id}`);
  }

  async deleteMultipleOrders(ids: string[]): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>('/orders/delete-multiple', { ids });
    return response.data.data;
  }

  async getOrdersByDeliveryDate(date: string): Promise<any[]> {
    const response = await this.api.get<ApiResponse<any[]>>(`/orders/delivery/${date}`);
    return response.data.data;
  }

  async getOrdersByStatus(status: string): Promise<any[]> {
    const response = await this.api.get<ApiResponse<any[]>>(`/orders/status/${status}`);
    return response.data.data;
  }

  // Generic methods
  async get<T>(endpoint: string): Promise<T> {
    const response = await this.api.get<ApiResponse<T>>(endpoint);
    return response.data.data;
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    const response = await this.api.post<ApiResponse<T>>(endpoint, data);
    return response.data.data;
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    const response = await this.api.put<ApiResponse<T>>(endpoint, data);
    return response.data.data;
  }

  async delete<T>(endpoint: string): Promise<T> {
    const response = await this.api.delete<ApiResponse<T>>(endpoint);
    return response.data.data;
  }

  // ============================================
  // LOST ORDERS (PEDIDOS EXTRAVIADOS)
  // ============================================

  async getAllLostOrders(startDate?: string, endDate?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/lost-orders${qs}`);
    return response.data.data;
  }

  async getLostOrderById(id: string): Promise<any> {
    const response = await this.api.get(`/lost-orders/${id}`);
    return response.data.data;
  }

  async createLostOrder(data: {
    orderNumber: string;
    carrier: string;
    shippingValue: string;
    orderValue: string;
    description?: string;
  }): Promise<any> {
    const response = await this.api.post('/lost-orders', data);
    return response.data.data;
  }

  async updateLostOrder(id: string, data: {
    orderNumber?: string;
    carrier?: string;
    shippingValue?: string;
    orderValue?: string;
    description?: string;
  }): Promise<any> {
    const response = await this.api.put(`/lost-orders/${id}`, data);
    return response.data.data;
  }

  async deleteLostOrder(id: string): Promise<any> {
    const response = await this.api.delete(`/lost-orders/${id}`);
    return response.data;
  }

  async deleteMultipleLostOrders(ids: string[]): Promise<any> {
    const response = await this.api.post('/lost-orders/delete-multiple', { ids });
    return response.data;
  }

  async getLostOrderStats(carrier?: string, startDate?: string, endDate?: string): Promise<any> {
    const params = new URLSearchParams();
    if (carrier) params.append('carrier', carrier);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/lost-orders/stats${qs}`);
    return response.data.data;
  }

  // ============================================
  // DELAYED ORDERS (PEDIDOS ATRASADOS)
  // ============================================

  async getAllDelayedOrders(startDate?: string, endDate?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/delayed-orders${qs}`);
    return response.data.data;
  }

  async getDelayedOrderById(id: string): Promise<any> {
    const response = await this.api.get(`/delayed-orders/${id}`);
    return response.data.data;
  }

  async createDelayedOrder(data: {
    orderNumber: string;
    carrier: string;
    shippingValue: string;
    description?: string;
  }): Promise<any> {
    const response = await this.api.post('/delayed-orders', data);
    return response.data.data;
  }

  async updateDelayedOrder(id: string, data: {
    orderNumber?: string;
    carrier?: string;
    shippingValue?: string;
    description?: string;
  }): Promise<any> {
    const response = await this.api.put(`/delayed-orders/${id}`, data);
    return response.data.data;
  }

  async deleteDelayedOrder(id: string): Promise<any> {
    const response = await this.api.delete(`/delayed-orders/${id}`);
    return response.data;
  }

  async deleteMultipleDelayedOrders(ids: string[]): Promise<any> {
    const response = await this.api.post('/delayed-orders/delete-multiple', { ids });
    return response.data;
  }

  async getDelayedOrderStats(carrier?: string, startDate?: string, endDate?: string): Promise<any> {
    const params = new URLSearchParams();
    if (carrier) params.append('carrier', carrier);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/delayed-orders/stats${qs}`);
    return response.data.data;
  }

  // ============================================
  // INTERNAL ERRORS (ERROS INTERNOS)
  // ============================================

  async getAllInternalErrors(
    startDate?: string,
    endDate?: string,
    options?: { mine?: boolean; responsibleUserId?: string },
  ): Promise<any[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (options?.mine) params.append('mine', 'true');
    if (options?.responsibleUserId) params.append('responsibleUserId', options.responsibleUserId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/internal-errors${qs}`);
    return response.data.data;
  }

  async getInternalErrorById(id: string): Promise<any> {
    const response = await this.api.get(`/internal-errors/${id}`);
    return response.data.data;
  }

  async createInternalError(data: {
    orderNumber: string;
    department: string;
    responsible: string;
    responsibleUserId?: string | null;
    description: string;
    errorValue: string;
  }): Promise<any> {
    const response = await this.api.post('/internal-errors', data);
    return response.data.data;
  }

  async updateInternalError(id: string, data: {
    orderNumber?: string;
    department?: string;
    responsible?: string;
    responsibleUserId?: string | null;
    description?: string;
    errorValue?: string;
  }): Promise<any> {
    const response = await this.api.put(`/internal-errors/${id}`, data);
    return response.data.data;
  }

  async deleteInternalError(id: string): Promise<any> {
    const response = await this.api.delete(`/internal-errors/${id}`);
    return response.data;
  }

  async deleteMultipleInternalErrors(ids: string[]): Promise<any> {
    const response = await this.api.post('/internal-errors/delete-multiple', { ids });
    return response.data;
  }

  async getInternalErrorStats(department?: string, responsible?: string, startDate?: string, endDate?: string): Promise<any> {
    const params = new URLSearchParams();
    if (department) params.append('department', department);
    if (responsible) params.append('responsible', responsible);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/internal-errors/stats${queryString}`);
    return response.data.data;
  }

  // ===== Verificação de Pedidos =====
  async getAllOrderVerifications(startDate?: string, endDate?: string, situacao?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (situacao) params.append('situacao', situacao);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/order-verifications${qs}`);
    return response.data.data;
  }

  // Retorna o registro completo, incluindo o anexo (base64) para visualização
  async getOrderVerificationById(id: string): Promise<any> {
    const response = await this.api.get(`/order-verifications/${id}`);
    return response.data.data;
  }

  async createOrderVerification(data: {
    orderNumber: string;
    phone: string;
    situacao?: string;
    observation?: string | null;
    attachment?: string | null;
    thumbnail?: string | null;
    attachmentName?: string | null;
  }): Promise<any> {
    const response = await this.api.post('/order-verifications', data);
    return response.data.data;
  }

  async updateOrderVerification(id: string, data: {
    orderNumber?: string;
    phone?: string;
    situacao?: string;
    observation?: string | null;
    attachment?: string | null;
    thumbnail?: string | null;
    attachmentName?: string | null;
  }): Promise<any> {
    const response = await this.api.put(`/order-verifications/${id}`, data);
    return response.data.data;
  }

  async deleteOrderVerification(id: string): Promise<any> {
    const response = await this.api.delete(`/order-verifications/${id}`);
    return response.data;
  }

  async deleteMultipleOrderVerifications(ids: string[]): Promise<any> {
    const response = await this.api.post('/order-verifications/delete-multiple', { ids });
    return response.data;
  }

  // ===== Reposição de Pedidos =====
  // pendingOnly: retorna apenas os que ainda NÃO estão "Finalizado" (usado no badge)
  async getAllOrderReplacements(
    startDate?: string,
    endDate?: string,
    situacao?: string,
    pendingOnly?: boolean,
  ): Promise<any[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (situacao) params.append('situacao', situacao);
    if (pendingOnly) params.append('pending', 'true');
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/order-replacements${qs}`);
    return response.data.data;
  }

  async getOrderReplacementById(id: string): Promise<any> {
    const response = await this.api.get(`/order-replacements/${id}`);
    return response.data.data;
  }

  async createOrderReplacement(data: {
    orderNumber: string;
    situacao?: string;
    observation?: string | null;
  }): Promise<any> {
    const response = await this.api.post('/order-replacements', data);
    return response.data.data;
  }

  async updateOrderReplacement(id: string, data: {
    orderNumber?: string;
    situacao?: string;
    observation?: string | null;
  }): Promise<any> {
    const response = await this.api.put(`/order-replacements/${id}`, data);
    return response.data.data;
  }

  async deleteOrderReplacement(id: string): Promise<any> {
    const response = await this.api.delete(`/order-replacements/${id}`);
    return response.data;
  }

  async deleteMultipleOrderReplacements(ids: string[]): Promise<any> {
    const response = await this.api.post('/order-replacements/delete-multiple', { ids });
    return response.data;
  }

  // ===== Pedidos Trocados =====
  // pendingOnly: retorna apenas os "Em análise" (usado no badge)
  async getAllOrderSwaps(
    startDate?: string,
    endDate?: string,
    situacao?: string,
    pendingOnly?: boolean,
  ): Promise<any[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (situacao) params.append('situacao', situacao);
    if (pendingOnly) params.append('pending', 'true');
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/order-swaps${qs}`);
    return response.data.data;
  }

  async getOrderSwapById(id: string): Promise<any> {
    const response = await this.api.get(`/order-swaps/${id}`);
    return response.data.data;
  }

  async createOrderSwap(data: {
    responsible: string;
    orderNumber: string;
    swappedOrderNumber: string;
    clientPhone1?: string | null;
    clientPhone2?: string | null;
    situacao?: string;
    observation?: string | null;
  }): Promise<any> {
    const response = await this.api.post('/order-swaps', data);
    return response.data.data;
  }

  async updateOrderSwap(id: string, data: {
    responsible?: string;
    orderNumber?: string;
    swappedOrderNumber?: string;
    clientPhone1?: string | null;
    clientPhone2?: string | null;
    situacao?: string;
    observation?: string | null;
  }): Promise<any> {
    const response = await this.api.put(`/order-swaps/${id}`, data);
    return response.data.data;
  }

  async deleteOrderSwap(id: string): Promise<any> {
    const response = await this.api.delete(`/order-swaps/${id}`);
    return response.data;
  }

  async deleteMultipleOrderSwaps(ids: string[]): Promise<any> {
    const response = await this.api.post('/order-swaps/delete-multiple', { ids });
    return response.data;
  }

  // ============================================
  // REFUND ORDERS (PEDIDOS ESTORNADOS)
  // ============================================

  async getAllRefundOrders(startDate?: string, endDate?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/refund-orders${qs}`);
    return response.data.data;
  }

  async getRefundOrderById(id: string): Promise<any> {
    const response = await this.api.get(`/refund-orders/${id}`);
    return response.data.data;
  }

  async createRefundOrder(data: {
    orderNumber: string;
    value: string;
    refundType: string;
    refundKey: string;
    status: string;
    reason: string;
    clientName: string;
    clientPhone: string;
  }): Promise<any> {
    const response = await this.api.post('/refund-orders', data);
    return response.data.data;
  }

  async updateRefundOrder(id: string, data: {
    orderNumber?: string;
    value?: string;
    refundType?: string;
    refundKey?: string;
    status?: string;
    reason?: string;
    clientName?: string;
    clientPhone?: string;
  }): Promise<any> {
    const response = await this.api.put(`/refund-orders/${id}`, data);
    return response.data.data;
  }

  async deleteRefundOrder(id: string): Promise<any> {
    const response = await this.api.delete(`/refund-orders/${id}`);
    return response.data;
  }

  async deleteMultipleRefundOrders(ids: string[]): Promise<any> {
    const response = await this.api.post('/refund-orders/delete-multiple', { ids });
    return response.data;
  }

  async getRefundOrderStats(refundType?: string, startDate?: string, endDate?: string): Promise<any> {
    const params = new URLSearchParams();
    if (refundType) params.append('refundType', refundType);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/refund-orders/stats${queryString}`);
    return response.data.data;
  }

  // ==================== SALES ====================

  async getAllSales(filters?: {
    sellerId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.sellerId) params.append('sellerId', filters.sellerId);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/sales${queryString}`);
    return response.data.data;
  }

  async getSaleById(id: string): Promise<any> {
    const response = await this.api.get(`/sales/${id}`);
    return response.data.data;
  }

  async createSale(data: {
    sellerId: string;
    clientPhone: string;
    freightValue: number;
    materialValue: number;
    saleDate: string;
    isFirstSale: boolean;
    observations?: string;
  }): Promise<any> {
    const response = await this.api.post('/sales', data);
    return response.data.data;
  }

  async updateSale(id: string, data: {
    clientPhone?: string;
    freightValue?: number;
    materialValue?: number;
    saleDate?: string;
    isFirstSale?: boolean;
    observations?: string;
  }): Promise<any> {
    const response = await this.api.put(`/sales/${id}`, data);
    return response.data.data;
  }

  async deleteSale(id: string): Promise<any> {
    const response = await this.api.delete(`/sales/${id}`);
    return response.data;
  }

  async deleteMultipleSales(ids: string[]): Promise<any> {
    const response = await this.api.post('/sales/delete-multiple', { ids });
    return response.data;
  }

  async getSellerStats(sellerId: string, month?: number, year?: number): Promise<any> {
    const params = new URLSearchParams();
    if (month) params.append('month', month.toString());
    if (year) params.append('year', year.toString());
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/sales/stats/${sellerId}${queryString}`);
    return response.data.data;
  }

  async finalizeDay(sellerId: string, date?: string): Promise<any> {
    const response = await this.api.post(`/sales/finalize/${sellerId}`, { date });
    return response.data.data;
  }

  async getDailySalesHistory(sellerId: string, month?: number, year?: number): Promise<any> {
    const params = new URLSearchParams();
    if (month) params.append('month', month.toString());
    if (year) params.append('year', year.toString());
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/sales/history/${sellerId}${queryString}`);
    return response.data.data;
  }

  async getBillingStats(params?: {
    month?: number;
    year?: number;
    startDate?: string; // YYYY-MM-DD
    endDate?: string;   // YYYY-MM-DD
  }): Promise<any> {
    const q = new URLSearchParams();
    if (params?.month) q.append('month', params.month.toString());
    if (params?.year) q.append('year', params.year.toString());
    if (params?.startDate) q.append('startDate', params.startDate);
    if (params?.endDate) q.append('endDate', params.endDate);
    const queryString = q.toString() ? `?${q.toString()}` : '';
    const response = await this.api.get(`/sales/billing${queryString}`);
    return response.data.data;
  }

  async setSellerTargets(month: number, year: number, targetValue: number): Promise<{ updated: number }> {
    const params = new URLSearchParams();
    params.append('month', month.toString());
    params.append('year', year.toString());
    const response = await this.api.put(`/sales/targets?${params.toString()}`, { targetValue });
    return response.data.data;
  }

  async getSellerBonusConfig(): Promise<{ tiers: Array<{ minSales: number; maxSales: number | null; rate: number; fixedBonus: number }> }> {
    const response = await this.api.get('/sales/bonus-config');
    return response.data.data ?? response.data;
  }

  async setSellerBonusConfig(config: { tiers: Array<{ minSales: number; maxSales: number | null; rate: number; fixedBonus: number }> }): Promise<void> {
    await this.api.put('/sales/bonus-config', config);
  }

  async getSiteMonthlyGoal(): Promise<number> {
    const response = await this.api.get('/sales/site-goal');
    return Number(response.data?.data?.goal ?? 25000);
  }

  async setSiteMonthlyGoal(goal: number): Promise<void> {
    await this.api.put('/sales/site-goal', { goal });
  }

  async getFullReport(month?: number, year?: number): Promise<any> {
    const params = new URLSearchParams();
    if (month) params.append('month', month.toString());
    if (year) params.append('year', year.toString());
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/sales/full-report${queryString}`);
    return response.data.data;
  }

  // ============================================
  // SETTINGS (CONFIGURAÇÕES)
  // ============================================

  async getAllSettings(): Promise<Record<string, string>> {
    const response = await this.api.get('/settings');
    return response.data.data;
  }

  async updateSettings(settings: Record<string, string>): Promise<any> {
    const response = await this.api.put('/settings', settings);
    return response.data.data;
  }

  async getSessionDuration(): Promise<{ duration: string; hours: string; days: string }> {
    const response = await this.api.get('/settings/session');
    return response.data.data;
  }

  async updateSessionDuration(hours: number, days: number): Promise<any> {
    const response = await this.api.put('/settings/session', { hours, days });
    return response.data.data;
  }

  // ============================================
  // INTEGRATIONS (INTEGRAÇÕES)
  // ============================================

  async getAllIntegrations(): Promise<any[]> {
    const response = await this.api.get('/settings/integrations');
    return response.data.data;
  }

  async createIntegration(data: {
    name: string;
    type: string;
    isEnabled?: boolean;
    config?: string;
  }): Promise<any> {
    const response = await this.api.post('/settings/integrations', data);
    return response.data.data;
  }

  async updateIntegration(id: string, data: {
    name?: string;
    type?: string;
    isEnabled?: boolean;
    config?: string;
  }): Promise<any> {
    const response = await this.api.put(`/settings/integrations/${id}`, data);
    return response.data.data;
  }

  async deleteIntegration(id: string): Promise<void> {
    await this.api.delete(`/settings/integrations/${id}`);
  }

  async toggleIntegration(id: string): Promise<any> {
    const response = await this.api.patch(`/settings/integrations/${id}/toggle`);
    return response.data.data;
  }

  // ============================================
  // IRROBA INTEGRATION
  // ============================================

  // Configuração
  async getIrrobaConfig(): Promise<any> {
    const response = await this.api.get('/integrations/irroba/config');
    return response.data;
  }

  async updateIrrobaConfig(config: {
    username: string;
    password: string;
    baseUrl: string;
  }): Promise<any> {
    const response = await this.api.put('/integrations/irroba/config', config);
    return response.data;
  }

  async testIrrobaConnection(): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post('/integrations/irroba/test');
    return response.data;
  }

  async getIrrobaWebhookUrls(): Promise<{ orders: string; products: string }> {
    const response = await this.api.get('/integrations/irroba/webhook-urls');
    return response.data;
  }

  // Pedidos do IRROBA
  async getIrrobaOrders(params?: {
    page?: number;
    limit?: number;
    status?: string;
    paymentStatus?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.paymentStatus) queryParams.append('paymentStatus', params.paymentStatus);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await this.api.get(`/integrations/irroba/orders${queryString}`);
    return response.data;
  }

  async getIrrobaRevenue(params?: { startDate?: string; endDate?: string }): Promise<{ total: number; count: number; totalPaid: number; countPaid: number }> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await this.api.get(`/integrations/irroba/revenue${queryString}`);
    return response.data;
  }

  /**
   * Estatísticas de vendas do site - OTIMIZADO (banco local)
   * Retorna totais agregados diretamente do banco, sem chamar API externa do Irroba.
   */
  async getSiteStats(month: number, year: number): Promise<{
    siteSalesTotal: number;
    siteOrdersTotal: number;
    ticketMedio: number;
    monthlyHistory: Array<{ month: string; year: number; total: number; count: number }>;
  }> {
    const params = new URLSearchParams();
    params.append('month', month.toString());
    params.append('year', year.toString());
    const response = await this.api.get(`/integrations/irroba/site-stats?${params.toString()}`, { timeout: 30000 });
    console.log('📊 getSiteStats response:', response.data);
    
    // Garantir que retornamos os dados corretamente
    const data = response.data?.data || response.data;
    return {
      siteSalesTotal: Number(data?.siteSalesTotal ?? 0),
      siteOrdersTotal: Number(data?.siteOrdersTotal ?? 0),
      ticketMedio: Number(data?.ticketMedio ?? 0),
      monthlyHistory: data?.monthlyHistory || [],
    };
  }

  async getIrrobaOrder(id: string): Promise<any> {
    const response = await this.api.get(`/integrations/irroba/orders/${id}`);
    return response.data;
  }

  async syncIrrobaOrders(startDate?: string, endDate?: string): Promise<{ synced: number; errors: number }> {
    const response = await this.api.post('/integrations/irroba/sync/orders', { startDate, endDate });
    return response.data;
  }

  // Produtos do IRROBA
  async getIrrobaProducts(params?: {
    page?: number;
    limit?: number;
    category?: string;
    active?: boolean;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.active !== undefined) queryParams.append('active', params.active.toString());
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await this.api.get(`/integrations/irroba/products${queryString}`);
    return response.data;
  }

  async syncIrrobaProducts(): Promise<{ synced: number; errors: number }> {
    const response = await this.api.post('/integrations/irroba/sync/products');
    return response.data;
  }

  // Pedidos e produtos sincronizados (salvos no CRM)
  async getSyncedIrrobaOrders(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<{ orders: any[]; pagination: any }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await this.api.get(`/integrations/irroba/synced/orders${queryString}`);
    return response.data;
  }

  async getSyncedIrrobaProducts(params?: {
    page?: number;
    limit?: number;
    category?: string;
    active?: boolean;
    search?: string;
  }): Promise<{ products: any[]; pagination: any }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.active !== undefined) queryParams.append('active', params.active.toString());
    if (params?.search) queryParams.append('search', params.search);
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await this.api.get(`/integrations/irroba/synced/products${queryString}`);
    return response.data;
  }

  // Status do IRROBA
  async getIrrobaStatuses(): Promise<any[]> {
    const response = await this.api.get('/integrations/irroba/statuses');
    return response.data;
  }

  // Logs de webhooks
  async getWebhookLogs(params?: {
    source?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.source) queryParams.append('source', params.source);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await this.api.get(`/integrations/webhooks/logs${queryString}`);
    return response.data;
  }

  async getWebhookStats(): Promise<{
    total: number;
    received: number;
    processed: number;
    errors: number;
  }> {
    const response = await this.api.get('/integrations/webhooks/stats');
    return response.data;
  }

  // ========================================
  // MULTI-STORE IRROBA
  // ========================================

  /**
   * Lista lojas IRROBA cadastradas
   */
  async listIrrobaStores(): Promise<any[]> {
    const response = await this.api.get('/integrations/irroba/stores');
    return response.data?.data || [];
  }

  /**
   * Cria uma nova loja IRROBA
   */
  async createIrrobaStore(data: {
    name: string;
    apiKey: string;
    apiSecret: string;
    apiUrl?: string;
    apiUsername?: string;
    isDefault?: boolean;
  }): Promise<any> {
    const response = await this.api.post('/integrations/irroba/stores', data);
    return response.data;
  }

  /**
   * Atualiza uma loja IRROBA
   */
  async updateIrrobaStore(id: string, data: Partial<{
    name: string;
    apiKey: string;
    apiSecret: string;
    apiUrl: string;
    apiUsername: string | null;
    isActive: boolean;
    isDefault: boolean;
  }>): Promise<any> {
    const response = await this.api.put(`/integrations/irroba/stores/${id}`, data);
    return response.data;
  }

  /**
   * Deleta uma loja IRROBA
   */
  async deleteIrrobaStore(id: string): Promise<any> {
    const response = await this.api.delete(`/integrations/irroba/stores/${id}`);
    return response.data;
  }

  /**
   * Lista mapeamentos de status → designer de uma loja
   */
  async listDesignerStatusMappings(storeId: string): Promise<any[]> {
    const response = await this.api.get(`/integrations/irroba/stores/${storeId}/designer-status`);
    return response.data?.data || [];
  }

  /**
   * Cria/atualiza mapeamento de status → designer
   */
  async upsertDesignerStatusMapping(storeId: string, data: {
    statusId: number;
    statusName: string;
    designerId: string;
  }): Promise<any> {
    const response = await this.api.post(`/integrations/irroba/stores/${storeId}/designer-status`, data);
    return response.data;
  }

  /**
   * Deleta mapeamento de status
   */
  async deleteDesignerStatusMapping(id: string): Promise<any> {
    const response = await this.api.delete(`/integrations/irroba/designer-status/${id}`);
    return response.data;
  }

  /**
   * Sincroniza pedidos de designers de uma loja
   * Timeout longo (5 min): a API do IRROBA pode demorar com muitos pedidos
   */
  async syncDesignerOrders(storeId: string, startDate?: string, endDate?: string): Promise<{
    synced: number;
    errors: number;
    syncedByDesigner?: Record<string, number>;
  }> {
    const response = await this.api.post(
      `/integrations/irroba/stores/${storeId}/sync-designer-orders`,
      { startDate, endDate },
      { timeout: 300000 } // 5 minutos
    );
    return response.data?.data || response.data;
  }

  /**
   * Sincroniza pedidos de TODAS as lojas e todos os designers
   * Timeout muito longo (10 min): múltiplas lojas × múltiplos designers
   */
  async syncAllDesignerOrders(startDate?: string, endDate?: string): Promise<{
    synced: number;
    errors: number;
    syncedByStore?: Record<string, number>;
    syncedByDesigner?: Record<string, number>;
  }> {
    const response = await this.api.post(
      '/integrations/irroba/stores/sync-all-designer-orders',
      { startDate, endDate },
      { timeout: 600000 } // 10 minutos
    );
    return response.data?.data || response.data;
  }

  /**
   * Lista designers disponíveis
   */
  async listDesigners(): Promise<Array<{ id: string; name: string; email: string }>> {
    const response = await this.api.get('/integrations/irroba/designers');
    return response.data?.data || [];
  }

  /**
   * Busca pedidos de um designer (de todas as lojas IRROBA).
   * Retorna objeto com data (lista), e quando bloqueado: blocked, reason, message (ex.: usuário inativo).
   */
  async getDesignerIrrobaOrders(designerId: string, params?: {
    status?: string;
    limit?: number;
  }): Promise<{ data: any[]; blocked?: boolean; reason?: string; message?: string }> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await this.api.get(`/integrations/irroba/designer/${designerId}/orders${queryString}`);
    const body = response.data || {};
    return {
      data: body.data || [],
      ...(body.blocked && { blocked: true, reason: body.reason, message: body.message }),
    };
  }

  /**
   * Busca status disponíveis de uma loja IRROBA
   */
  async getStoreStatuses(storeId: string): Promise<Array<{ id: number; name: string }>> {
    const response = await this.api.get(`/integrations/irroba/stores/${storeId}/statuses`);
    return response.data?.data || [];
  }

  /**
   * Atualiza workStatus e/ou artCount de um pedido IRROBA
   */
  async updateStoreOrderStatus(orderId: string, data: { workStatus?: string; artCount?: number; storeId?: string; priority?: string }): Promise<any> {
    const response = await this.api.patch(`/integrations/irroba/store-order/${orderId}`, data);
    return response.data?.data || response.data;
  }

  // ===== Artes por fora (Shopee etc.) — total por dia do designer =====
  async getDesignerExternalArts(designerId: string, date: string): Promise<{ designerId: string; date: string; count: number }> {
    const response = await this.api.get(`/designer-external-arts/day`, { params: { designerId, date } });
    return response.data.data;
  }

  async setDesignerExternalArts(designerId: string, date: string, count: number): Promise<{ designerId: string; date: string; count: number }> {
    const response = await this.api.put(`/designer-external-arts/day`, { designerId, date, count });
    return response.data.data;
  }

  /**
   * Finaliza o dia do designer (salva histórico de artes)
   */
  async finalizeDesignerDay(designerId: string, date: string, artsCount?: number): Promise<any> {
    const response = await this.api.post(`/integrations/irroba/designer/${designerId}/finalize-day`, {
      date,
      artsCount,
    });
    return response.data?.data || response.data;
  }

  /**
   * Baixa a logo do cliente de um pedido IRROBA (abre em nova aba ou faz download)
   */
  async downloadOrderLogo(storeId: string, irrobaOrderId: string, fileId: string): Promise<void> {
    const path = `integrations/irroba/stores/${storeId}/orders/${irrobaOrderId}/logo/${encodeURIComponent(fileId)}`;
    const response = await this.api.get(path, { responseType: 'blob' });
    const blob = response.data as Blob;
    const contentType = (response.headers?.['content-type'] || '').toLowerCase();
    // Backend retorna JSON { url } para links externos
    if (contentType.includes('application/json')) {
      const text = await blob.text();
      const data = JSON.parse(text);
      if (data?.url && typeof data.url === 'string') {
        window.open(data.url, '_blank', 'noopener,noreferrer');
        return;
      }
    }
    // Arquivo binário — fazer download
    const disposition = response.headers?.['content-disposition'];
    let filename = 'logo-cliente';
    if (typeof disposition === 'string') {
      const match = disposition.match(/filename[*]?=['"]?(?:UTF-8'')?([^'";\n]+)['"]?/i);
      if (match) filename = match[1].trim();
    }
    // Remover sufixo de hash do Irroba (ex: .png.7ee2d18a... → .png)
    filename = filename.replace(/\.([a-f0-9]{32}|[a-f0-9]{40})$/i, '');
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  }

  /**
   * Busca histórico diário de produtividade de um designer
   */
  async getDesignerDailyHistory(designerId: string, month?: number, year?: number): Promise<any> {
    const params = new URLSearchParams();
    if (month) params.append('month', month.toString());
    if (year) params.append('year', year.toString());
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/integrations/irroba/designer/${designerId}/daily-history${queryString}`);
    return response.data?.data || response.data;
  }

  // ============================================
  // NOTIFICATIONS (NOTIFICAÇÕES)
  // ============================================

  async getNotifications(limit: number = 50, unreadOnly: boolean = false): Promise<any[]> {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    if (unreadOnly) params.append('unreadOnly', 'true');
    const response = await this.api.get(`/notifications?${params.toString()}`);
    return response.data.data;
  }

  async getUnreadNotificationsCount(): Promise<number> {
    try {
      const response = await this.api.get('/notifications/unread-count');
      return response.data?.data?.count ?? 0;
    } catch {
      return 0;
    }
  }

  async markNotificationAsRead(id: string): Promise<any> {
    const response = await this.api.patch(`/notifications/${id}/read`);
    return response.data.data;
  }

  async markAllNotificationsAsRead(): Promise<any> {
    const response = await this.api.patch('/notifications/read-all');
    return response.data.data;
  }

  async deleteNotification(id: string): Promise<any> {
    const response = await this.api.delete(`/notifications/${id}`);
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
